import { LoadingButton } from "@mui/lab";
import {
  Box,
  Chip,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { ReactNode, useContext, useEffect, useState } from "react";

import LocationAutocomplete from "components/LocationAutocomplete";
import {
  DiscoverSearchGeopoint,
  UserLocation,
} from "components/LocationAutocomplete/schema";

/* ── Static content ───────────────────────────────────────────────── */

const MAX_SEARCH_QUERIES = 5;
const MAX_LOCATIONS = 5;

const TIPS = [
  `Add up to ${MAX_SEARCH_QUERIES} search queries to find different types of businesses`,
  'Examples: "organic grocery stores", "boutique hotels", "yoga studios", "beauty spas"',
  "Use a smaller search radius (5-10 km) for dense urban areas, larger (25-50 km) for suburban or rural areas",
];

const DISTANCE_OPTIONS = [
  { value: 5, label: "5 km (3 mi)", hint: "Walkable neighborhood" },
  { value: 10, label: "10 km (6 mi)", hint: "City center" },
  { value: 15, label: "15 km (9 mi)", hint: "Greater city" },
  { value: 20, label: "20 km (12 mi)", hint: "Urban area" },
  { value: 25, label: "25 km (16 mi)", hint: "Recommended" },
  { value: 30, label: "30 km (19 mi)", hint: "Metro area" },
  { value: 40, label: "40 km (25 mi)", hint: "Wide regional" },
  { value: 50, label: "50 km (31 mi)", hint: "Max radius" },
];

const RATING_OPTIONS = [
  { value: 0, label: "Any rating" },
  { value: 3.0, label: "3+ stars" },
  { value: 3.5, label: "3.5+ stars" },
  { value: 4.0, label: "4+ stars (Recommended)" },
  { value: 4.5, label: "4.5+ stars" },
];

/* ── Shared sx tokens ─────────────────────────────────────────────── */

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    "& fieldset": { borderColor: "#e5e7eb", borderWidth: 2 },
    "&:hover fieldset": { borderColor: "primary.main" },
    "&.Mui-focused fieldset": { borderColor: "primary.main" },
  },
  "& .MuiInputBase-input": {
    fontSize: 16,
    py: 1.5,
    px: 2,
    "&::placeholder": { color: "rgba(1,67,57,0.5)", opacity: 1 },
  },
};

const locationAutocompleteSx = {
  border: "2px solid #e5e7eb",
  borderRadius: "10px",
  height: 52,
  py: 1.5,
  px: 2,
  width: "100%",
  fontSize: 16,
  boxSizing: "border-box",
  "&:hover, &:focus": {
    outline: "none",
    borderColor: "primary.main",
  },
};

const selectSx = {
  width: "100%",
  borderRadius: "10px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e5e7eb",
    borderWidth: 2,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "primary.main",
  },
};

/* ── Sub-components ───────────────────────────────────────────────── */

function FilterSection({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1 }}>
        <Typography
          sx={{ fontWeight: 500, fontSize: 14, color: "text.primary" }}
        >
          {label}
          {required && (
            <Typography
              component="span"
              sx={{ color: "error.main", ml: 0.5, fontSize: 14 }}
            >
              *
            </Typography>
          )}
        </Typography>
        {hint && (
          <Typography sx={{ fontSize: 11, color: "grey.400", fontWeight: 500 }}>
            {hint}
          </Typography>
        )}
      </Box>
      {children}
    </Box>
  );
}

function AndDivider() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", my: 1 }}>
      <Box sx={{ flex: 1, height: "1px", backgroundColor: "#e5e7eb" }} />
      <Typography
        sx={{
          px: 1.5,
          fontSize: 11,
          fontWeight: 700,
          color: "grey.400",
          letterSpacing: 1,
        }}
      >
        AND
      </Typography>
      <Box sx={{ flex: 1, height: "1px", backgroundColor: "#e5e7eb" }} />
    </Box>
  );
}

function OrChipGroup({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
      {children}
    </Box>
  );
}

/* ── Main component ───────────────────────────────────────────────── */

interface ConfigureGooglePlacesProps {
  draftId: number;
  onNext: (config: Record<string, any>) => Promise<void>;
  initialConfig?: Record<string, any> | null;
}

export default function ConfigureGooglePlaces({
  draftId: _draftId,
  onNext,
  initialConfig,
}: ConfigureGooglePlacesProps) {
  const { discoverTab } = useContext(DiscoverViewContext);
  const sidebarOpen = discoverTab !== null;
  const [geopoints, setGeopoints] = useState<DiscoverSearchGeopoint[]>(
    initialConfig?.geopoints ?? [],
  );
  const [searchQueries, setSearchQueries] = useState<string[]>(
    initialConfig?.search_queries ?? [],
  );
  const [queryInput, setQueryInput] = useState("");
  const [radiusKm, setRadiusKm] = useState<number>(
    initialConfig?.radius_km ?? 25,
  );
  const [minRating, setMinRating] = useState<number>(
    initialConfig?.min_rating ?? 0,
  );
  const [locationError, setLocationError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Sync if initialConfig changes (e.g. draft fetch completes)
  useEffect(() => {
    if (initialConfig?.geopoints) {
      setGeopoints(initialConfig.geopoints);
    }
    if (initialConfig?.search_queries != null) {
      setSearchQueries(initialConfig.search_queries);
    }
    if (initialConfig?.radius_km != null) {
      setRadiusKm(initialConfig.radius_km);
    }
    if (initialConfig?.min_rating != null) {
      setMinRating(initialConfig.min_rating);
    }
  }, [initialConfig]);

  const [loading, setLoading] = useState(false);

  /* ── Location handlers ── */

  const handleLocationChange = (userLocation: UserLocation) => {
    if (!userLocation?.latitude || !userLocation?.longitude) return;

    if (geopoints.length >= MAX_LOCATIONS) {
      setLocationError(
        `You can add at most ${MAX_LOCATIONS} locations. Remove one to add another.`,
      );
      return;
    }

    const addressComponents = userLocation.address_components;

    // Require city-level or more specific
    const hasLocality = addressComponents?.some(
      (x) =>
        x?.types?.includes("locality") ||
        x?.types?.includes("administrative_area_level_3"),
    );

    if (!hasLocality) {
      setLocationError(
        "Please select a city or more specific location. Countries and states/provinces are too broad.",
      );
      return;
    }

    setLocationError(null);
    setFieldErrors((prev) => {
      const { locations: _, ...rest } = prev;
      return rest;
    });

    const city = addressComponents?.find(
      (x) =>
        x?.types?.includes("administrative_area_level_3") ||
        x?.types?.includes("locality"),
    )?.longText;
    const region = addressComponents?.find((x) =>
      x?.types?.includes("administrative_area_level_1"),
    )?.longText;

    const label = city
      ? region
        ? `${city}, ${region}`
        : city
      : addressComponents?.[0]?.longText;

    const newGeopoint: DiscoverSearchGeopoint = {
      country_code: userLocation.country_code,
      latitude: userLocation.latitude,
      longitude: userLocation.longitude,
      is_country: false,
      label,
    };

    const isDuplicate = geopoints.some(
      (g) =>
        g.latitude === newGeopoint.latitude &&
        g.longitude === newGeopoint.longitude,
    );
    if (!isDuplicate) {
      setGeopoints((prev) => [...prev, newGeopoint]);
    }
  };

  const removeGeopoint = (index: number) => {
    setGeopoints((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNext = async () => {
    // Auto-submit pending search query input (split by comma)
    let finalSearchQueries = [...searchQueries];
    const pendingRaw = queryInput.trim();
    if (pendingRaw) {
      const parts = pendingRaw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      for (const part of parts) {
        if (finalSearchQueries.length >= MAX_SEARCH_QUERIES) break;
        if (!finalSearchQueries.includes(part)) {
          finalSearchQueries = [...finalSearchQueries, part];
        }
      }
      setSearchQueries(finalSearchQueries);
      setQueryInput("");
    }

    // Validate required fields
    const errors: Record<string, string> = {};
    if (finalSearchQueries.length === 0) {
      errors.searchQueries = "Please add at least one search query.";
    } else if (finalSearchQueries.length > MAX_SEARCH_QUERIES) {
      errors.searchQueries = `You can add at most ${MAX_SEARCH_QUERIES} search queries.`;
    }
    if (geopoints.length === 0) {
      errors.locations = "Please add at least one location.";
    } else if (geopoints.length > MAX_LOCATIONS) {
      errors.locations = `You can add at most ${MAX_LOCATIONS} locations.`;
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    setLoading(true);
    try {
      const config: Record<string, any> = {
        geopoints,
        search_queries: finalSearchQueries,
        radius_km: radiusKm,
      };
      if (minRating > 0) {
        config.min_rating = minRating;
      }
      await onNext(config);
    } catch {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 976, mx: "auto" }}>
      {/* Source header */}
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            backgroundColor: "#d5f0e5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="span"
            className="fa-solid fa-location-dot"
            sx={{ fontSize: 18, color: "#1a7a52" }}
          />
        </Box>
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 24,
              color: "text.primary",
              lineHeight: "32px",
            }}
          >
            Local Business Search
          </Typography>
          <Typography
            sx={{ fontSize: 16, color: "text.secondary", lineHeight: "24px" }}
          >
            Discover local businesses in specific areas using Google Places
          </Typography>
        </Box>
      </Box>

      {/* Two-column layout */}
      <Box sx={{ display: "flex", gap: 3 }}>
        {/* ── Left panel: form card ── */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "14px",
            p: 3,
          }}
        >
          {/* Search Queries */}
          <FilterSection
            label="Search Queries"
            required
            hint={`${searchQueries.length}/${MAX_SEARCH_QUERIES} • press Enter to add`}
          >
            <TextField
              fullWidth
              placeholder={
                searchQueries.length >= MAX_SEARCH_QUERIES
                  ? `Limit reached (${MAX_SEARCH_QUERIES} max)`
                  : "e.g., boutique hotels"
              }
              value={queryInput}
              onChange={(e) => {
                setQueryInput(e.target.value);
                setFieldErrors((prev) => {
                  const { searchQueries: _, ...rest } = prev;
                  return rest;
                });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const parts = queryInput
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean);
                  const valid = parts.filter((p) => !searchQueries.includes(p));
                  if (valid.length > 0) {
                    setSearchQueries((prev) => {
                      const remaining = MAX_SEARCH_QUERIES - prev.length;
                      if (remaining <= 0) return prev;
                      const unique = valid.filter((v) => !prev.includes(v));
                      return [...prev, ...unique.slice(0, remaining)];
                    });
                  }
                  setQueryInput("");
                }
              }}
              disabled={searchQueries.length >= MAX_SEARCH_QUERIES}
              error={!!fieldErrors.searchQueries}
              sx={textFieldSx}
            />
            {fieldErrors.searchQueries ? (
              <Typography sx={{ fontSize: 12, color: "error.main", mt: 0.5 }}>
                {fieldErrors.searchQueries}
              </Typography>
            ) : (
              <Typography sx={{ fontSize: 12, color: "grey.500", mt: 0.5 }}>
                Add the types of businesses you want to find
              </Typography>
            )}
            {searchQueries.length > 0 && (
              <OrChipGroup>
                {searchQueries.map((q, i) => (
                  <Chip
                    key={`query-${i}`}
                    label={q}
                    size="small"
                    icon={
                      <Box
                        component="i"
                        className="fa-solid fa-magnifying-glass"
                        sx={{ fontSize: 11 }}
                      />
                    }
                    onDelete={() =>
                      setSearchQueries((prev) =>
                        prev.filter((_, idx) => idx !== i),
                      )
                    }
                  />
                ))}
              </OrChipGroup>
            )}
          </FilterSection>

          <AndDivider />

          {/* Locations */}
          <FilterSection
            label="Locations"
            required
            hint={`${geopoints.length}/${MAX_LOCATIONS} • city-level or below`}
          >
            <Box
              sx={{ mb: 1 }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <LocationAutocomplete
                handleLocationChange={handleLocationChange}
                customStyling={{
                  ...locationAutocompleteSx,
                  opacity: geopoints.length >= MAX_LOCATIONS ? 0.5 : 1,
                  pointerEvents:
                    geopoints.length >= MAX_LOCATIONS ? "none" : "auto",
                }}
                placeholderText={
                  geopoints.length >= MAX_LOCATIONS
                    ? `Limit reached (${MAX_LOCATIONS} max)`
                    : "Search for a city or neighborhood"
                }
              />
            </Box>
            {locationError && (
              <Typography sx={{ fontSize: 12, color: "error.main", mb: 1 }}>
                {locationError}
              </Typography>
            )}
            {fieldErrors.locations ? (
              <Typography sx={{ fontSize: 12, color: "error.main", mb: 1 }}>
                {fieldErrors.locations}
              </Typography>
            ) : (
              <Typography sx={{ fontSize: 12, color: "grey.500", mb: 1 }}>
                Add cities or neighborhoods where you want to find businesses
              </Typography>
            )}

            {geopoints.length > 0 && (
              <OrChipGroup>
                {geopoints.map((g, i) => (
                  <Chip
                    key={`geo-${i}`}
                    label={g.label || "Location"}
                    size="small"
                    icon={
                      <Box
                        component="i"
                        className="fa-solid fa-location-dot"
                        sx={{ fontSize: 11 }}
                      />
                    }
                    onDelete={() => removeGeopoint(i)}
                  />
                ))}
              </OrChipGroup>
            )}
          </FilterSection>

          <AndDivider />

          {/* Search Distance & Minimum Rating — side by side */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <FilterSection label="Search Distance" hint="from each location">
                <Select<number>
                  value={radiusKm}
                  onChange={(e) => setRadiusKm(Number(e.target.value))}
                  input={<OutlinedInput />}
                  sx={selectSx}
                >
                  {DISTANCE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                      <Typography
                        component="span"
                        sx={{ ml: 1, fontSize: 12, color: "grey.400" }}
                      >
                        {opt.hint}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FilterSection>
            </Box>

            <Box sx={{ flex: 1 }}>
              <FilterSection label="Minimum Rating">
                <Select<number>
                  value={minRating}
                  onChange={(e) => setMinRating(Number(e.target.value))}
                  input={<OutlinedInput />}
                  sx={selectSx}
                >
                  {RATING_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FilterSection>
            </Box>
          </Box>

          {/* Next button */}
          <LoadingButton
            variant="contained"
            fullWidth
            loading={loading}
            onClick={handleNext}
            sx={{
              mt: 3,
              borderRadius: "10px",
              backgroundColor: "primary.main",
              fontWeight: 600,
              fontSize: 16,
              py: 1.5,
              textTransform: "none",
            }}
          >
            Next
          </LoadingButton>
        </Box>

        {/* ── Right panel: tips & info ── */}
        <Box
          sx={{
            width: 310,
            flexShrink: 0,
            display: sidebarOpen ? "none" : { xs: "none", md: "flex" },
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Tips card */}
          <Box
            sx={{
              backgroundColor: "#fffbeb",
              border: "1px solid #fee685",
              borderRadius: "14px",
              p: 2,
            }}
          >
            <Box
              sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1.5 }}
            >
              <Box
                component="span"
                className="fa-solid fa-lightbulb"
                sx={{ fontSize: 18, color: "#7b3306" }}
              />
              <Typography
                sx={{ fontWeight: 600, fontSize: 18, color: "#7b3306" }}
              >
                Tips
              </Typography>
            </Box>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {TIPS.map((tip, i) => (
                <Box
                  component="li"
                  key={i}
                  sx={{
                    color: "#e17100",
                    mb: 1,
                    "&::marker": { color: "#e17100" },
                  }}
                >
                  <Typography
                    sx={{ fontSize: 14, color: "#7b3306", lineHeight: "20px" }}
                  >
                    {tip}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* How it works card */}
          <Box
            sx={{
              backgroundColor: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: "14px",
              p: 2,
            }}
          >
            <Typography
              sx={{ fontWeight: 600, fontSize: 14, color: "#166534", mb: 1 }}
            >
              How searches work
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              <Box component="li" sx={{ color: "#15803d", mb: 0.5 }}>
                <Typography
                  sx={{ fontSize: 13, color: "#15803d", lineHeight: "20px" }}
                >
                  Each search query finds its own set of results
                </Typography>
              </Box>
              <Box component="li" sx={{ color: "#15803d" }}>
                <Typography
                  sx={{ fontSize: 13, color: "#15803d", lineHeight: "20px" }}
                >
                  Every query is searched across all your locations
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
